import { ATTRIBUTES, NO, YES } from '../constants';
import { FAIL_SAFE_MAPPING } from './MapFilterValues.pipe';
import { Locales, LocalizedAttributesValueInterface } from '../interfaces/LocalizedAttributesValueInterface';
import { AttributesClassInterface } from '../interfaces/AttributesClassInterface';
import { LocalesEnum } from '../enums/LocalesEnum';
import { createIsCompetitor } from '../createIsCompetitor';

const positiveValues = FAIL_SAFE_MAPPING.filter(([, v]) => v).map(([v]) => v);

const mapFailSafeValueToYesNo = (
    localizedValue: LocalizedAttributesValueInterface,
    rawName: Locales,
): LocalizedAttributesValueInterface => {
    const positiveValuesArray = (localizedValue?.[rawName] || [])
    .filter((val: string) => positiveValues.includes(val));
    const isNegative = positiveValuesArray.length === 0;
    const raw = isNegative ? [NO] : [YES];
    return { [rawName]: raw };
};

type ObjectWithCompetitorAttr = Pick<AttributesClassInterface, `_${ATTRIBUTES.COMPETITOR_PRODUCT}`>;

const isRetrofit = createIsCompetitor<
    LocalizedAttributesValueInterface,
    ObjectWithCompetitorAttr,
    keyof ObjectWithCompetitorAttr
>(`_${ATTRIBUTES.COMPETITOR_PRODUCT}`);

const isEmpty = (value: any) => value === '' || value === null || value === undefined;
const arrayNotEmpty = (value: any) => Array.isArray(value) && value.length > 0
const NOLocalizedValue = (rawName: Locales): LocalizedAttributesValueInterface => ({ [rawName]: [NO] });

export const NumberPart = /^\s*([0-9.,]+).*/gm;

const calcDpv = (kvs: number, volumetricFlow: number): number => {
    return Math.pow(volumetricFlow / kvs, 2) * 100;
}
const calcDpvAm = (kvs: number, volumetricFlow: number): number => {
    return Math.pow(volumetricFlow / kvs, 2);
}

const calcVelocity = (DN: number, volumetricFlow: number): number => {
    return (volumetricFlow / 3600) / (Math.PI * Math.pow(DN / 2000, 2));
}
const calcVelocityAm = (DN: number, volumetricFlow: number): number => {
    return 0.4085 * (volumetricFlow / Math.pow(DN, 2));
}

const calcClipPositionPIQCV = (
    clips: string[][],
    rawName: LocalesEnum,
    volumetricFlow: string,
): string | null => {
    if (volumetricFlow.length === 0) {
        return null;
    }
    const positionsFiltered: string[][] = clips
    .filter(([_, val]) => val.length > 0);

    if (positionsFiltered.length < 2) {
        return null;
    }

    const positions: [string, number][] = positionsFiltered.map(([name, val]) => {
        const valNumber = Number(val.replace(NumberPart, '$1'));
        return [
            name,
            rawName === LocalesEnum.en_US ? valNumber : valNumber / 1000,
        ]
    });
    const volumetricFlowNumber = Number(volumetricFlow);
    const found = positions.find(([_, val]) => val >= volumetricFlowNumber);
    return found ? found[0] : 'NC';
}

const clipPositionsQCV_EU = (attributes: AttributesClassInterface, rawName: LocalesEnum) => [
    ['1', (attributes['_8681']?.[rawName] || []).join('')],
    ['2', (attributes['_8682']?.[rawName] || []).join('')],
    ['3', (attributes['_8683']?.[rawName] || []).join('')],
    ['4', (attributes['_8684']?.[rawName] || []).join('')],
    ['5', (attributes['_8685']?.[rawName] || []).join('')],
    ['6', (attributes['_8686']?.[rawName] || []).join('')],
    ['N', (attributes['_8687']?.[rawName] || []).join('')],
    ['NC', (attributes['_14']?.[rawName] || []).join('')],
];

const clipPositionsQCV_AM = (attributes: AttributesClassInterface, rawName: LocalesEnum) => [
    ['1', (attributes['_6341']?.[rawName] || []).join('')],
    ['2', (attributes['_6342']?.[rawName] || []).join('')],
    ['3-', (attributes['_6349']?.[rawName] || []).join('')],
    ['3', (attributes['_6343']?.[rawName] || []).join('')],
    ['4', (attributes['_6344']?.[rawName] || []).join('')],
    ['4+', (attributes['_6350']?.[rawName] || []).join('')],
    ['5', (attributes['_6345']?.[rawName] || []).join('')],
    ['5+', (attributes['_6351']?.[rawName] || []).join('')],
    ['6', (attributes['_6346']?.[rawName] || []).join('')],
    ['N', (attributes['_6347']?.[rawName] || []).join('')],
    ['NC', (attributes['_6348']?.[rawName] || []).join('')],
]

const calcClipPositionQCV = (
    positions: string[][],
    kvs: string,
): string | null => {
    const kvsNumber = Number(kvs);

    const positionsFiltered = positions.filter(([_, val]) => val.length > 0);

    if (positionsFiltered.length < 2) {
        return null;
    }

    const closest = positionsFiltered
    .map<[string, number]>((item) => {
        const [name, val] = item;
        return [
            name,
            Number(val.replace(NumberPart, '$1')),
        ];
    })
    .reduce((prev, curr) => {
        return (Math.abs(curr[1] - kvsNumber) < Math.abs(prev[1] - kvsNumber) ? curr : prev);
    });
    return closest[0];
}

const calcKvsClipPositionQCV = (
    positions: string[][],
    kvs: string,
): string | null => {
    const kvsNumber = Number(kvs);

    const positionsFiltered = positions.filter(([_, val]) => val.length > 0);

    if (positionsFiltered.length < 2) {
        return null;
    }

    const closest = positionsFiltered
    .map<[string, number, string]>((item) => {
        const [name, val] = item;
        return [
            name,
            Number(val.replace(NumberPart, '$1')),
            val,
        ];
    })
    .reduce((prev, curr) => {
        return (Math.abs(curr[1] - kvsNumber) < Math.abs(prev[1] - kvsNumber) ? curr : prev);
    });
    return closest ? closest[2] : null;
}

export class AttributesClassResolver {

    getClipPositionPIQCV_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string
    ): string | null {
        return calcClipPositionPIQCV(
            [
                ['2-', (attributes['_6322']?.[rawName] || []).join('')],
                ['2', (attributes['_6323']?.[rawName] || []).join('')],
                ['2+', (attributes['_6325']?.[rawName] || []).join('')],
                ['3-', (attributes['_6326']?.[rawName] || []).join('')],
                ['3', (attributes['_6327']?.[rawName] || []).join('')],
                ['3+', (attributes['_6328']?.[rawName] || []).join('')],
                ['4-', (attributes['_6329']?.[rawName] || []).join('')],
                ['4', (attributes['_6330']?.[rawName] || []).join('')],
                ['4+', (attributes['_6331']?.[rawName] || []).join('')],
                ['5-', (attributes['_6332']?.[rawName] || []).join('')],
                ['5', (attributes['_6333']?.[rawName] || []).join('')],
                ['5+', (attributes['_6334']?.[rawName] || []).join('')],
                ['6-', (attributes['_6335']?.[rawName] || []).join('')],
                ['6', (attributes['_6336']?.[rawName] || []).join('')],
                ['6+', (attributes['_6337']?.[rawName] || []).join('')],
                ['N-', (attributes['_6338']?.[rawName] || []).join('')],
                ['N', (attributes['_6339']?.[rawName] || []).join('')],
            ],
            rawName,
            volumetricFlow,
        );
    }

    getClipPositionPIQCV_EU(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string
    ): string | null {
        return calcClipPositionPIQCV(
            [
                ['1', (attributes['_548']?.[rawName] || []).join('')],
                ['2', (attributes['_549']?.[rawName] || []).join('')],
                ['3', (attributes['_7311']?.[rawName] || []).join('')],
                ['3+', (attributes['_8669']?.[rawName] || []).join('')],
                ['4-', (attributes['_8670']?.[rawName] || []).join('')],
                ['4', (attributes['_8671']?.[rawName] || []).join('')],
                ['4+', (attributes['_8672']?.[rawName] || []).join('')],
                ['5-', (attributes['_8673']?.[rawName] || []).join('')],
                ['5', (attributes['_8674']?.[rawName] || []).join('')],
                ['5+', (attributes['_8675']?.[rawName] || []).join('')],
                ['6-', (attributes['_8676']?.[rawName] || []).join('')],
                ['6', (attributes['_8677']?.[rawName] || []).join('')],
                ['6+', (attributes['_8678']?.[rawName] || []).join('')],
                ['N-', (attributes['_8679']?.[rawName] || []).join('')],
                ['N', (attributes['_8680']?.[rawName] || []).join('')],
            ],
            rawName,
            volumetricFlow,
        );
    }

    getClipPosition_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }

        return calcClipPositionQCV(
            clipPositionsQCV_AM(attributes, rawName),
            volumetricFlow,
        );
    }

    getClipPositionKvs_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        kvs: string
    ): string | null {
        if (kvs.length === 0) {
            return null;
        }

        return calcKvsClipPositionQCV(
            clipPositionsQCV_AM(attributes, rawName),
            kvs,
        );
    }

    getClipPosition_EU(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }

        return calcClipPositionQCV(
            clipPositionsQCV_EU(attributes, rawName),
            volumetricFlow,
        );
    }

    getClipPositionKvs_EU(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        kvs: string
    ): string | null {
        if (kvs.length === 0) {
            return null;
        }

        return calcKvsClipPositionQCV(
            clipPositionsQCV_EU(attributes, rawName),
            kvs,
        );
    }

    calcMinReqDpvFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            const kvsTheor = attributes['_6827']?.[rawName] || [];
            if (kvsTheor.length === 0) {
                return null;
            }
            return calcDpv(
                Number(kvsTheor.join('').replace(NumberPart, '$1')),
                Number(volumetricFlow),
            ).toFixed(0) + ' kPa';
        } catch (e) {
            return null;
        }
    };

    calcEffectiveDpvFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvsUser: string,
        isOCCO: boolean,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            const positionsMap = clipPositionsQCV_EU(attributes, rawName);
            const pos = calcClipPositionQCV(positionsMap, kvsUser);
            const kvsPos: string | null = positionsMap.reduce(
                (acc, [p, v]) => p === pos ? v : acc,
                null as string | null
            );
            const shortValveType = '_6667'
            const isBFV = !!attributes[shortValveType]
                && attributes[shortValveType] !== null
                && attributes[shortValveType]?.[rawName] !== undefined
                && Array.isArray(attributes[shortValveType]![rawName])
                && attributes[shortValveType]![rawName]!.includes('BFV');
            const kvsAttribute = isOCCO && isBFV ? '_78' : '_14';
            const kvs = kvsPos && kvsPos.length > 0 ? kvsPos : (attributes[kvsAttribute]?.[rawName] || []).join('');
            if (kvs.length === 0) {
                return null;
            }
            return Math.ceil(calcDpv(
                Number(kvs.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            )) + ' kPa';
        } catch (e) {
            return null;
        }
    };

    calcEffectiveDpv6wayFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvsAttribute: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            // @ts-ignore
            const kvs = (attributes[kvsAttribute]?.[rawName] || []).join('');
            if (kvs.length === 0) {
                return null;
            }
            return Math.ceil(calcDpv(
                Number(kvs.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            )) + ' kPa';
        } catch (e) {
            return null;
        }
    };

    calcEffectiveDpv6wayFunction_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvsAttribute: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            // @ts-ignore
            const kvs = (attributes[kvsAttribute]?.[rawName] || []).join('');
            if (kvs.length === 0) {
                return null;
            }
            return calcDpvAm(
                Number(kvs.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            ).toFixed(1) + ' psi';
        } catch (e) {
            return null;
        }
    };

    calcEffectiveDpvFunction_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvsUser: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            const positionsMap = clipPositionsQCV_AM(attributes, rawName);
            const pos = calcClipPositionQCV(positionsMap, kvsUser);
            const kvsPos: string | null = positionsMap.reduce(
                (acc, [p, v]) => p === pos ? v : acc,
                null as string | null
            );
            const kvs = kvsPos && kvsPos.length > 0 ? kvsPos : (attributes['_148']?.[rawName] || []).join('');
            if (kvs.length === 0) {
                return null;
            }
            return calcDpvAm(
                Number(kvs.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            ).toFixed(1) + ' psi';
        } catch (e) {
            return null;
        }
    };

    calcEffectivePVFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvs: string,
        dpmv: string
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        if (kvs === '' && dpmv === '') {
            return '1';
        }

        try {

            const positionsMap = rawName === LocalesEnum.en_US ?
                clipPositionsQCV_AM(attributes, rawName) : clipPositionsQCV_EU(attributes, rawName);
            const pos = calcClipPositionQCV(positionsMap, kvs);
            const kvsPos: string | null = positionsMap.reduce(
                (acc, [p, v]) => p === pos ? v : acc,
                null as string | null
            );

            const kvsAttr = kvsPos && kvsPos.length > 0 ? kvsPos : (attributes['_14']?.[rawName] || []).join('');
            if (kvsAttr.length === 0) {
                return null;
            }
            const effectiveDpv = calcDpv(
                Number(kvsAttr.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            );

            const dpv = dpmv.length > 0 ? Number(dpmv) : (calcDpv(
                Number(kvs),
                Number(volumetricFlow),
            ) * 0.5) / 0.5;
            return (effectiveDpv / (effectiveDpv + dpv)).toFixed(2);
        } catch (e) {
            return null;
        }
    };
    calcEffectivePV6wayFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
        kvs: string,
        dpmv: string,
        kvsAttribute: string
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        if (kvs === '' && dpmv === '') {
            return '1';
        }

        try {

            // @ts-ignore
            const kvsAttr = (attributes[kvsAttribute]?.[rawName] || []).join('');
            if (kvsAttr.length === 0) {
                return null;
            }
            const effectiveDpv = calcDpv(
                Number(kvsAttr.replace(NumberPart, '$1')),
                Number(volumetricFlow),
            );

            const dpv = dpmv.length > 0 ? Number(dpmv) : (calcDpv(
                Number(kvs),
                Number(volumetricFlow),
            ) * 0.5) / 0.5;
            return (effectiveDpv / (effectiveDpv + dpv)).toFixed(2);
        } catch (e) {
            return null;
        }
    };

    calcFlowVelocityFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            const dn = attributes['_15']?.[rawName] || [];
            if (dn.length === 0) {
                return null;
            }
            return calcVelocity(
                Number(dn.join('').replace(NumberPart, '$1')),
                Number(volumetricFlow),
            ).toFixed(1) + ' m/s';
        } catch (e) {
            return null;
        }
    };

    calcFlowVelocityFunction_AM(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
        volumetricFlow: string,
    ): string | null {
        if (volumetricFlow.length === 0) {
            return null;
        }
        try {
            const dn = attributes['_880']?.[rawName] || [];
            if (dn.length === 0) {
                return null;
            }
            return calcVelocityAm(
                Number(dn.join('').replace(NumberPart, '$1')),
                Number(volumetricFlow),
            ).toFixed(1) + ' ft/s';
        } catch (e) {
            return null;
        }
    };

    async failSafeFunction(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
    ): Promise<LocalizedAttributesValueInterface | null> {
        const value = attributes['_7683'];
        if (!value) {
            return isRetrofit(attributes, rawName) ? NOLocalizedValue(rawName) : null;
        }
        return mapFailSafeValueToYesNo(value, rawName);
    }

    async failSafeFunctionSolution(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
    ): Promise<LocalizedAttributesValueInterface | null> {
        const value = attributes['_8160'];
        if (!value) {
            return isRetrofit(attributes, rawName)
                ? null
                : NOLocalizedValue(rawName);
        }
        return mapFailSafeValueToYesNo(value, rawName);
    }

    async integratedSwitch(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
    ): Promise<LocalizedAttributesValueInterface | null> {
        const value = attributes['_7684'];
        const isEmptyValue = isEmpty(value)
            || (!isEmpty(value) && !arrayNotEmpty(value?.[rawName] || []));
        if (isEmptyValue) {
            return isRetrofit(attributes, rawName)
                ? NOLocalizedValue(rawName)
                : null;
        }
        return (value?.[rawName] || []).includes('No')
            ? NOLocalizedValue(rawName)
            : value;
    }

    async integratedSwitchSolution(
        attributes: AttributesClassInterface,
        rawName: LocalesEnum,
    ): Promise<LocalizedAttributesValueInterface | null> {
        const value = attributes['_112'];
        const isEmptyValue = isEmpty(value)
            || (!isEmpty(value) && !arrayNotEmpty(value?.[rawName] || []));
        if (isEmptyValue) {
            return isRetrofit(attributes, rawName)
                ? null
                : NOLocalizedValue(rawName);
        }
        return (value?.[rawName] || []).includes('No')
            ? NOLocalizedValue(rawName)
            : value;
    }
}
